import { type RefObject, useEffect, useState } from "react";

import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  SIDE_BY_SIDE_QUERY,
  SIDE_BY_SIDE_TRIGGER_MARGIN,
  STACKED_ZONE,
  STACKED_ZONE_MAX_VIEWPORT,
} from "@/views/shop/sections/configurator/imageColumn";
import {
  CONFIGURATOR_VISUALS,
  type ConfiguratorBlockId,
} from "@/views/shop/shopContent";

const BLOCK_IDS: readonly string[] = CONFIGURATOR_VISUALS.blocks.map(
  ({ id }) => id,
);

const isBlockId = (value: string | undefined): value is ConfiguratorBlockId =>
  value !== undefined && BLOCK_IDS.includes(value);

/**
 * The options block crossing the trigger line, Tesla's scroll-linked pattern:
 * each `[data-config-block]` inside `rootRef` is watched against a thin band
 * (40% down the viewport beside the zone, just under the zone when stacked,
 * where it pins to the top of the screen), and the one crossing it is
 * active. Between blocks the last one
 * holds, and above them all the first, so the zone is never left without a
 * set. IntersectionObserver only; nothing listens to scroll.
 */
export const useActiveBlock = (
  rootRef: RefObject<HTMLElement | null>,
): ConfiguratorBlockId => {
  const sideBySide = useMediaQuery(SIDE_BY_SIDE_QUERY) === true;
  const tablet = useMediaQuery("(min-width: 800px)") === true;
  const [active, setActive] = useState<ConfiguratorBlockId>(
    CONFIGURATOR_VISUALS.blocks[0].id,
  );

  useEffect(() => {
    const root = rootRef.current;
    if (root === null) return;

    const zone = tablet ? STACKED_ZONE.tablet : STACKED_ZONE.phone;
    // The stacked zone's foot, from the top of the screen it pins to.
    const stackedLine = Math.min(
      (document.documentElement.clientWidth - 2 * zone.margin) / zone.ratio,
      zone.maxHeight,
      window.innerHeight * STACKED_ZONE_MAX_VIEWPORT,
    );
    // A 1% band just under the zone's foot.
    const stackedMargin = `-${stackedLine}px 0px -${Math.max(
      window.innerHeight - stackedLine - window.innerHeight * 0.01,
      0,
    )}px 0px`;
    const observer = new IntersectionObserver(
      (entries) => {
        // The last crossing entry wins, which is the one the scroll reached.
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (!(entry.target instanceof HTMLElement)) continue;

          const id = entry.target.dataset["configBlock"];
          if (!isBlockId(id)) continue;
          // Stacked, the zone stays on the model's films past the model
          // block: the combination, add-ons and checkout don't take it over
          // (the add-ons show in their details dialog instead).
          setActive(
            !sideBySide && id !== "title" && id !== "model" ? "model" : id,
          );
        }
      },
      {
        rootMargin: sideBySide ? SIDE_BY_SIDE_TRIGGER_MARGIN : stackedMargin,
      },
    );

    root
      .querySelectorAll<HTMLElement>("[data-config-block]")
      .forEach((block) => observer.observe(block));

    return () => observer.disconnect();
  }, [rootRef, sideBySide, tablet]);

  return active;
};
